import React, { useState } from 'react';
import { FiMail, FiCheck, FiX, FiSettings } from 'react-icons/fi';
import api from '../../services/api';

const EmailTestPage = () => {
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailTest, setEmailTest] = useState({ email: '', subject: 'Test Email', message: 'This is a test email from TaskFlowPro.' });
  const [emailResult, setEmailResult] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [manualConfig, setManualConfig] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_pass: '',
    email_from: '',
    secure: false
  });



  const testSMTPConnection = async () => {
    // Validate required fields
    if (!manualConfig.smtp_host || !manualConfig.smtp_port || !manualConfig.smtp_user || !manualConfig.smtp_pass) {
      setTestResult({ success: false, data: { message: 'Please fill in all SMTP configuration fields' } });
      return;
    }
    
    try {
      setLoading(true);
      setTestResult(null);
      console.log('Testing SMTP with config:', manualConfig);
      const response = await api.post('/dev-tools/smtp/test-manual-public', manualConfig);
      console.log('SMTP test response:', response.data);
      setTestResult({ success: true, data: response.data });
    } catch (error) {
      console.error('SMTP Connection Test Error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = 'Connection failed';
      if (error.response?.status === 404) {
        errorMessage = 'Email testing service not available. Please check server configuration.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setTestResult({ 
        success: false, 
        data: { 
          message: errorMessage,
          status: 'Failed',
          error: error.response?.data?.error || error.message
        } 
      });
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    // Validate required fields
    if (!manualConfig.smtp_host || !manualConfig.smtp_port || !manualConfig.smtp_user || !manualConfig.smtp_pass || !manualConfig.email_from || !emailTest.email) {
      setEmailResult({ success: false, data: { message: 'Please fill in all SMTP configuration fields and recipient email' } });
      return;
    }
    
    try {
      setSendingEmail(true);
      setEmailResult(null);
      const payload = { ...emailTest, ...manualConfig };
      console.log('Sending test email with payload:', payload);
      const response = await api.post('/dev-tools/smtp/send-test-manual-public', payload);
      console.log('Email send response:', response.data);
      setEmailResult({ success: true, data: response.data });
    } catch (error) {
      console.error('Send Test Email Error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = 'Failed to send email';
      if (error.response?.status === 404) {
        errorMessage = 'Email testing service not available. Please check server configuration.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setEmailResult({ 
        success: false, 
        data: { 
          message: errorMessage,
          status: 'Failed',
          error: error.response?.data?.error || error.message
        } 
      });
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <FiMail className="mr-3 text-blue-500" size={24} />
        <h1 className="text-2xl font-semibold">Email Testing Tool</h1>
      </div>

      {/* SMTP Configuration */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center mb-4">
          <FiSettings className="mr-2 text-gray-500" />
          <h2 className="text-lg font-medium">SMTP Configuration</h2>
        </div>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
              <input
                type="text"
                value={manualConfig.smtp_host}
                onChange={(e) => setManualConfig({...manualConfig, smtp_host: e.target.value})}
                placeholder="smtp.gmail.com"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Port</label>
              <input
                type="number"
                value={manualConfig.smtp_port}
                onChange={(e) => setManualConfig({...manualConfig, smtp_port: e.target.value})}
                placeholder="587"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SMTP User</label>
              <input
                type="email"
                value={manualConfig.smtp_user}
                onChange={(e) => setManualConfig({...manualConfig, smtp_user: e.target.value})}
                placeholder="your-email@gmail.com"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Password</label>
              <input
                type="password"
                value={manualConfig.smtp_pass}
                onChange={(e) => setManualConfig({...manualConfig, smtp_pass: e.target.value})}
                placeholder="your-app-password"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email From</label>
              <input
                type="email"
                value={manualConfig.email_from}
                onChange={(e) => setManualConfig({...manualConfig, email_from: e.target.value})}
                placeholder="noreply@yourcompany.com"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={manualConfig.secure}
                  onChange={(e) => setManualConfig({...manualConfig, secure: e.target.checked})}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Use SSL/TLS (Port 465)</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-medium mb-4">Test SMTP Connection</h2>
        <button
          onClick={testSMTPConnection}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Testing...
            </>
          ) : (
            <>
              <FiMail className="mr-2" />
              Test Connection
            </>
          )}
        </button>
      </div>

      {/* Send Test Email */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-medium mb-4">Send Test Email</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
            <input
              type="email"
              value={emailTest.email}
              onChange={(e) => setEmailTest({...emailTest, email: e.target.value})}
              placeholder="Enter email address to test"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              type="text"
              value={emailTest.subject}
              onChange={(e) => setEmailTest({...emailTest, subject: e.target.value})}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={emailTest.message}
              onChange={(e) => setEmailTest({...emailTest, message: e.target.value})}
              rows="3"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          <button
            onClick={sendTestEmail}
            disabled={sendingEmail || !emailTest.email}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center"
          >
            {sendingEmail ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <FiMail className="mr-2" />
                Send Test Email
              </>
            )}
          </button>
        </div>
      </div>

      {/* SMTP Test Results */}
      {testResult && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-medium mb-4">SMTP Connection Results</h2>
          
          <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center mb-2">
              {testResult.success ? (
                <FiCheck className="text-green-500 mr-2" size={20} />
              ) : (
                <FiX className="text-red-500 mr-2" size={20} />
              )}
              <span className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {testResult.data.status || (testResult.success ? 'Success' : 'Failed')}
              </span>
            </div>
            
            <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
              {testResult.data.message}
            </p>
            
            {testResult.data.error && (
              <p className="text-sm text-red-600 mt-2">
                <strong>Error:</strong> {testResult.data.error}
              </p>
            )}
            
            {testResult.data.details && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-2">Configuration Details:</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Host:</strong> {testResult.data.details.smtp_host}</p>
                  <p><strong>Port:</strong> {testResult.data.details.smtp_port}</p>
                  <p><strong>User:</strong> {testResult.data.details.smtp_user}</p>
                  <p><strong>Secure:</strong> {testResult.data.details.secure ? 'Yes' : 'No'}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Send Results */}
      {emailResult && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-4">Email Send Results</h2>
          
          <div className={`p-4 rounded-lg ${emailResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center mb-2">
              {emailResult.success ? (
                <FiCheck className="text-green-500 mr-2" size={20} />
              ) : (
                <FiX className="text-red-500 mr-2" size={20} />
              )}
              <span className={`font-medium ${emailResult.success ? 'text-green-800' : 'text-red-800'}`}>
                {emailResult.success ? 'Email Sent Successfully' : 'Email Send Failed'}
              </span>
            </div>
            
            <p className={`text-sm ${emailResult.success ? 'text-green-700' : 'text-red-700'}`}>
              {emailResult.data.message}
            </p>
            
            {emailResult.data.error && (
              <p className="text-sm text-red-600 mt-2">
                <strong>Error:</strong> {emailResult.data.error}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTestPage;